import React from 'react';
import { store } from '../../store/store';
import { showNotification } from '../../store/notificationSlice';

/**
 * Error boundary that specifically catches ChunkLoadErrors and reloads the page.
 * This is a common issue with code splitting where the browser tries to load 
 * an old chunk that no longer exists on the server.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ChunkErrorBoundary caught error:', error, errorInfo);

    // Check if it's a ChunkLoadError
    // Webpack's ChunkLoadError usually has name="ChunkLoadError" or 
    // message contains "Loading chunk... failed"
    const isChunkLoadError =
      error.name === 'ChunkLoadError' ||
      /Loading (CSS )?chunk .* failed/i.test(error.message || '') ||
      /Failed to fetch dynamically imported module/i.test(error.message || '');

    // Route errors through the existing top-right notification UI
    const notify = (message, title = 'Error') => {
      try {
        store.dispatch(
          showNotification({
            message,
            title,
            severity: 'error',
          })
        );
      } catch (e) {
        // If notification dispatch fails, don't break error recovery
        console.error('ChunkErrorBoundary failed to dispatch notification:', e);
      }
    };

    if (isChunkLoadError) {
      // Avoid infinite reload loops by checking session storage
      const chunkErrorKey = 'last_chunk_error_reload';
      const lastReload = sessionStorage.getItem(chunkErrorKey);
      const now = Date.now();

      // Only reload if the last reload was more than 10 seconds ago
      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem(chunkErrorKey, now.toString());
        console.warn('ChunkLoadError detected. Reloading page to recover...');
        notify('We updated the app. Reloading to recover the latest UI…', 'App updated');
        // Give the notification a moment to render before the reload.
        setTimeout(() => window.location.reload(), 400);
      } else {
        console.error('ChunkLoadError occurred again shortly after reload. Stopping redirect loop.');
        notify('This page is having trouble loading. Please refresh again.', 'Load failed');
      }
    }

    // For non-chunk errors, redirect away from the broken view
    if (!isChunkLoadError) {
      const redirectKey = 'last_chunk_error_redirect';
      const lastRedirect = sessionStorage.getItem(redirectKey);
      const now = Date.now();

      if (!lastRedirect || now - parseInt(lastRedirect) > 10000) {
        sessionStorage.setItem(redirectKey, now.toString());
        notify('Something went wrong. Redirecting you to a safe page…', 'Unexpected error');

        // Redirect without a full reload so the notification remains visible.
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
        // Allow the UI to render again after reroute.
        this.setState({ hasError: false, error: null });
      } else {
        // If we keep erroring, do a hard reload to recover cleanly.
        notify('Something went wrong again. Reloading…', 'Unexpected error');
        setTimeout(() => window.location.reload(), 400);
      }
    }
  }

  render() {
    // Never render the raw error UI ("junk"). We instead recover via reload/redirect
    // and surface the error through the top-right notification system.
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
